import React, { useEffect, useCallback } from 'react'

import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { FILTER_OPTIONS } from 'utils/Product'
import { usePolygons } from 'hooks/polygons'
import messages from 'services/intl/messageDefinitions'
import { useLocale } from 'stores/UserStore'
import { RegionalDiscussionStore } from 'stores/RegionalDiscussionStore'
import { Map, WithMapReady } from 'components/Map/Map'
import { Sidebar } from 'components/Sidebar/Sidebar'
import { Details } from 'components/RegionalDiscussions/Details'
import { Summary } from 'components/Product/Summary'
import { CreateForm } from 'components/RegionalDiscussions/CreateForm'
import { MultiProductPolygon } from 'components/Map/MultiProductPolygon'
import { TwoColLayoutPrimaryRight } from 'components/TwoColumnLayout/TwoColLayoutPrimaryRight'
import { UnassignedPolygons } from 'components/Map/UnassignedPolygons'
import {
    putRegionalDiscussion,
    cloneAllRegionalDiscussions,
} from 'services/RegionalDiscussions'
import { MapStatusBar } from 'components/MapStatusBar/MapStatusBar'
import { ProductStatusBarText } from 'components/Product/ProductStatusBarText'
import { CloneAllButton } from 'components/Product/CloneAllButton'
import { Accessibility } from 'components/Accessibility'
import { MultipleProductsTable } from 'components/Map/Accessibility/MultipleProductsTable'
import { useActiveRegionalDiscussion, useFilteredRegionalDiscussions, useRegionalDiscussions, useUnassignedPolygons } from 'hooks/regionalDiscussions'

export const RegionalDiscussions = () => {
    const [queryParams] = useSearchParams()
    const locale = useLocale()
    const polygons = usePolygons()
    const filters = RegionalDiscussionStore.useFilters()
    const addFilter = RegionalDiscussionStore.useAddFilter()
    const setActiveRegionalDiscussionId = RegionalDiscussionStore.useSetActiveProductId()
    const activeRegionalDiscussion = useActiveRegionalDiscussion()
    const unassignedCount = useUnassignedPolygons()
    const queryID = queryParams.get('focus') || ''
    const disableSetupButton = filters[0] !== 'draft'
    const intl = useIntl()
    const { regionalDiscussions, mutate } = useRegionalDiscussions()

    useEffect(() => {
        document.title = intl.formatMessage(messages.regionaldiscussion)
    }, [])

    useEffect(() => {
        if (queryID) {
            setActiveRegionalDiscussionId(queryID)
        }
        filters.forEach((filter) => {
            // default filter
            addFilter(filter)
        })
        if (activeRegionalDiscussion && activeRegionalDiscussion.id === queryID) {
            addFilter(activeRegionalDiscussion.status)
        }
    }, [regionalDiscussions])

    const standardA11yComponent = (
        <>
            <Map locale={locale}>
                <WithMapReady>
                    {polygons && polygons.features && (
                        <>
                            <MultiProductPolygon
                                useEditing={RegionalDiscussionStore.useEditingProductSetup}
                                useActiveProduct={useActiveRegionalDiscussion}
                                apiCall={putRegionalDiscussion}
                                polygons={polygons}
                                mutateProducts={mutate}
                                useDraftProduct={RegionalDiscussionStore.useDraftProduct}
                                useUpdateDraftProduct={RegionalDiscussionStore.useUpdateDraftProduct}
                                allProducts={regionalDiscussions}
                                useFilteredProducts={useFilteredRegionalDiscussions}
                                useAssignedPolygons={useFilteredRegionalDiscussions}
                                productSource="regionalDiscussion"
                            />
                        </>
                    )}
                </WithMapReady>
            </Map>
            <UnassignedPolygons unassignedCount={unassignedCount} />
            {activeRegionalDiscussion && (
                <MapStatusBar
                    product={activeRegionalDiscussion}
                    text={<ProductStatusBarText product={activeRegionalDiscussion} />}
                />
            )}
        </>
    )

    const enhancedA11yComponent = polygons && polygons.features && (
        <MultipleProductsTable
            useEditing={RegionalDiscussionStore.useEditingProductSetup}
            unassignedCount={unassignedCount}
            useActiveItem={useActiveRegionalDiscussion}
            useProducts={useFilteredRegionalDiscussions}
            putRequest={putRegionalDiscussion}
            mutateProducts={mutate}
            polygons={polygons}
            useDraftItem={RegionalDiscussionStore.useDraftProduct}
            useUpdateDraftItem={RegionalDiscussionStore.useUpdateDraftProduct}
        />
    )

    return (
        <TwoColLayoutPrimaryRight
            left={
                <Accessibility
                    standardA11yComponent={standardA11yComponent}
                    enhancedA11yComponent={enhancedA11yComponent}
                />
            }
            right={
                <Sidebar
                    title={'regionalDiscussions'}
                    emptyMessage={'noRegionalDiscussionsFound'}
                    Details={Details}
                    Summary={Summary}
                    dataProvider={useFilteredRegionalDiscussions}
                    filterProvider={RegionalDiscussionStore.useFilters}
                    listData={regionalDiscussions}
                    filterOptions={FILTER_OPTIONS}
                    activeFilters={filters}
                    useAddFilter={RegionalDiscussionStore.useAddFilter}
                    useRemoveFilter={RegionalDiscussionStore.useRemoveFilter}
                    useClearActiveID={RegionalDiscussionStore.useClearActiveProductId}
                    createLabel={'createRegionalDiscussion'}
                    disableSetupButton={disableSetupButton}
                    useSetActiveId={RegionalDiscussionStore.useSetActiveProductId}
                    useSetEditing={RegionalDiscussionStore.useSetEditingProductSetup}
                    useSetEditingContent={RegionalDiscussionStore.useSetEditingProductContent}
                    useResetDraft={RegionalDiscussionStore.useResetDraftProduct}
                    CreateForm={CreateForm}
                    mutateProducts={mutate}
                    useActiveItem={useActiveRegionalDiscussion}
                    radioStyleFiltering={true}
                    buttons={
                        <CloneAllButton
                            mutateProducts={mutate}
                            useFilters={RegionalDiscussionStore.useFilters}
                            useAddFilter={RegionalDiscussionStore.useAddFilter}
                            cloneAllApiCall={cloneAllRegionalDiscussions}
                            buttonId={'cloneAllButton'}
                            confirmationText="cloneAllYes"
                        />
                    }
                ></Sidebar>
            }
        />
    )
}
